/**
 * The composer's hooks, for any record (`/api/composer/<recordType>/<uuid>/`).
 *
 * - `useComposerTemplates` - the templates of one kind the record can send:
 *   the ones its workflow sends first, then the library.
 * - `useComposerTemplatePreview` - a template read against the record, not sent.
 * - `useComposerSendPreview` - what a send would dispatch, and who it can go to.
 * - `useComposerSend` - send it.
 *
 * The record type is a key the backend registers (`claim` today); nothing here
 * knows what a claim is. Who may send what is checked on the server, by kind.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import api from '@/admin/lib/api'

export type RecipientKind = 'consumer' | 'contractor' | 'third_party' | string

export interface ComposerSendPreview {
  template: {
    id: number
    slug: string
    name: string
    kind: string
    subject: string
  }
  trigger_uuid: string
  available_recipient_kinds: RecipientKind[]
  /** kind -> 'internal' | 'external' | 'unknown'. Platform taxonomy, served. */
  recipient_audiences?: Record<string, 'internal' | 'external' | 'unknown'>
  /** kind -> the tenant's own word for it, from their entity labels. */
  recipient_labels?: Record<string, string>
  /** Everyone the record names, resolved on the server. */
  parties?: Party[]
  /** Whether the tenant has SMS enabled (controls the "also text" option). */
  sms_available?: boolean
  /** Whether letters can go by post: the tenant has a mailroom inbox. */
  mail_available?: boolean
}

/** One addressee the record itself names, resolved on the server. */
export interface Party {
  /** Stable key the send resolves again, e.g. customer or contact:<uuid>. */
  key: string
  kind: string
  /** The tenant's word for who this is, e.g. Homeowner or a contact role. */
  label: string
  name: string
  email: string
  /** Postal address, one line per part. Empty when the record holds none. */
  address: string
  audience: 'internal' | 'external'
}

export interface ComposerRecipient {
  kind: RecipientKind
  /** Required when kind = 'third_party'. */
  email?: string
  /** Optional sender-supplied display name (third_party). */
  name?: string
  /** Where to post it (third_party, by Mail). */
  postal_address?: string
}

/** How a letter reaches its addressee. */
export type LetterDelivery = 'email' | 'mail'

export interface ComposerResultRow {
  kind: RecipientKind
  email: string
  name: string
  matched: boolean
  suppressed: boolean
  suppressed_reason: string | null
  communication_log_id: number | null
  upload_session_id: number | null
  error: string | null
}

export interface ComposerSmsResult {
  status: string
  reason: string
  communication_log_id: number | null
}

export interface ComposerSendResponse {
  template_slug: string
  results: ComposerResultRow[]
  sms?: ComposerSmsResult | null
}

/** A kind of record messages are sent from, in the tenant's words. */
export interface ComposerRecordType {
  value: string
  label: string
  /** A template naming no record type is offered on this one. */
  offers_unscoped: boolean
  /** The kinds of template sent from this kind of record. */
  kinds: ComposeKind[]
}

export function useComposerRecordTypes() {
  return useQuery<ComposerRecordType[]>({
    queryKey: ['composer-record-types'],
    queryFn: async () => {
      const res = await api.get('/composer/record-types/')
      return res.data
    },
  })
}

const base = (recordType: string, recordUuid: string | undefined) =>
  `/composer/${recordType}/${recordUuid}`

export function useComposerSendPreview(
  recordType: string,
  recordUuid: string | undefined,
  /** Compose a chosen template instead of the one the record's state resolves to. */
  templateId?: number | null,
) {
  return useQuery<ComposerSendPreview | null>({
    queryKey: ['composer-send-preview', recordType, recordUuid, templateId ?? null],
    enabled: !!recordUuid,
    queryFn: async () => {
      try {
        const res = await api.get(
          `${base(recordType, recordUuid)}/send/preview/`,
          templateId ? { params: { template_id: templateId } } : undefined,
        )
        return res.data
      } catch (err: unknown) {
        // 409 = nothing picked and no trigger for the record's state. A normal
        // "nothing to send" state, not an error to surface.
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status === 409) return null
        throw err
      }
    },
  })
}

export interface ComposerSendOptions {
  /** Toast on success; defaults to the packet wording. */
  successMessage?: (sent: number) => string
  /** Leave error toasts to the caller, e.g. to highlight fields. */
  handleErrors?: boolean
}

export function useComposerSend(
  recordType: string,
  recordUuid: string | undefined,
  options: ComposerSendOptions = {},
) {
  const qc = useQueryClient()
  const { successMessage, handleErrors = true } = options
  return useMutation<
    ComposerSendResponse,
    unknown,
    {
      recipients: ComposerRecipient[]
      send_sms?: boolean
      template_id?: number | null
      edit?: LetterEdit
      /** Parties picked from the record; the server resolves their addresses. */
      party_keys?: string[]
      /** Answers to the template's fill-ins, keyed by prompt key. */
      prompt_values?: Record<string, string>
      /** Files to send with a letter: the record's own, or uploads for this send. */
      attachment_uuids?: string[]
      /** Mail posts the letter through the tenant's mailroom. */
      delivery?: LetterDelivery
    }
  >({
    mutationFn: async ({
      recipients,
      send_sms,
      template_id,
      edit,
      party_keys,
      prompt_values,
      attachment_uuids,
      delivery,
    }) => {
      const res = await api.post(`${base(recordType, recordUuid)}/send/`, {
        recipients,
        send_sms,
        ...(template_id ? { template_id } : {}),
        ...(edit?.subject_override ? { subject_override: edit.subject_override } : {}),
        ...(edit?.body_html_override ? { body_html_override: edit.body_html_override } : {}),
        ...(headerPayload(edit?.letter_header)
          ? { letter_header: headerPayload(edit?.letter_header) }
          : {}),
        ...(party_keys?.length ? { party_keys } : {}),
        ...(prompt_values && Object.keys(prompt_values).length ? { prompt_values } : {}),
        ...(attachment_uuids?.length ? { attachment_uuids } : {}),
        ...(delivery === 'mail' ? { delivery } : {}),
      })
      return res.data
    },
    onSuccess: (data) => {
      const sent = data.results.filter((r) => r.matched && !r.suppressed).length
      const suppressed = data.results.filter((r) => r.suppressed).length
      const failed = data.results.filter((r) => r.error).length

      const sms = data.sms
      const smsSent = sms?.status === 'sent'
      const smsNote = sms
        ? smsSent
          ? ' Customer also texted.'
          : ` SMS not sent (${sms.reason}).`
        : ''

      if (data.results.length === 0 && sms) {
        // SMS-only send (no email recipients picked).
        if (smsSent) toast.success('Upload link texted to the customer.')
        else toast.error(`SMS not sent (${sms.reason}).`)
      } else if (failed) {
        toast.error(
          `Sent ${sent} of ${data.results.length} - ${failed} failed. ` +
            'Check the activity for details.',
        )
      } else if (suppressed) {
        toast.success(
          `Sent ${sent}. ${suppressed} suppressed (cooldown / already sent recently).${smsNote}`,
        )
      } else if (successMessage) {
        toast.success(`${successMessage(sent)}${smsNote}`)
      } else {
        toast.success(`Email packet sent to ${sent} recipient(s).${smsNote}`)
      }
      // The record's log and activity have a new row, and a sent letter files
      // a note on the record.
      qc.invalidateQueries({ queryKey: [recordType, recordUuid] })
      qc.invalidateQueries({ queryKey: ['communications-activity'] })
      qc.invalidateQueries({ queryKey: ['record-notes', recordType, recordUuid] })
    },
    onError: () => {
      if (handleErrors) toast.error("Couldn't send the email packet. Try again.")
    },
  })
}

// ── The template library, scoped to one record ──────────────────────

/** Which kind of template the composer is sending. */
export type ComposeKind = 'letter' | 'evidence_request' | 'notification'

/** A compose-time fill-in, referenced in the template as `{{ prompts.<key> }}`. */
export interface LetterPrompt {
  key: string
  label: string
  type?: 'text' | 'number' | 'enum'
  options?: string[]
  required?: boolean
}

export interface ComposerTemplate {
  id: number
  slug: string
  name: string
  subject: string
  kind: string
  tags: string[]
  output_modes: string[]
  prompts: LetterPrompt[]
  /** The tenant's own word for when this template is used, from their workflow. */
  when_label: string
  /** Whether this template leaves the tenant. Served, never inferred here. */
  audience: 'internal' | 'external' | 'unknown'
  is_tenant_owned: boolean
  is_default: boolean
}

export interface ComposerTemplateGroup {
  key: string
  label: string
  /** The record's own workflow: its letters are the ones to reach for first. */
  is_current?: boolean
  items: ComposerTemplate[]
}

export interface ComposerTemplates {
  kind?: string
  default_template_id: number | null
  groups: ComposerTemplateGroup[]
}

/**
 * Templates of one kind this record can send: the ones its workflow sends
 * first, then the rest of the library. Grouping and the "when to use" labels
 * are the tenant's configuration.
 */
export function useComposerTemplates(
  recordType: string,
  recordUuid: string | undefined,
  kind?: ComposeKind,
) {
  return useQuery<ComposerTemplates>({
    queryKey: ['composer-templates', recordType, recordUuid, kind ?? 'all'],
    enabled: !!recordUuid,
    queryFn: async () => {
      const res = await api.get(
        `${base(recordType, recordUuid)}/templates/`,
        kind ? { params: { kind } } : undefined,
      )
      return res.data ?? { default_template_id: null, groups: [] }
    },
  })
}

export interface ComposerTemplatePreview {
  template_id: number
  kind?: string
  subject: string
  body_html: string
  body_text: string
  /** The letter PDF, base64. Empty for anything that is not a letter. */
  pdf_base64?: string
  /** The authored source, for the editor to start from. */
  source_subject: string
  source_body_html: string
  /** The letter as it will read, merge fields filled in: what a sender types over. */
  rendered_body_html?: string
  /** What a letter prints above its body before anyone edits it. */
  header?: LetterHeaderDefaults
  /** Whose paper a letter is on. */
  letterhead?: Letterhead
}

/** The date, addressee, address and reference a letter prints above its body. */
export interface LetterHeader {
  /** ISO date, YYYY-MM-DD. */
  date?: string
  name?: string
  address?: string
  reference?: string
}

export interface LetterHeaderDefaults extends Required<LetterHeader> {
  /** The tenant's word for the record the reference names, e.g. Claim. */
  reference_label: string
}

export interface Letterhead {
  name: string
  address: string
  logo_url: string
}

/** A sender's one-off edit. Applies to this letter only, never the template. */
export interface LetterEdit {
  subject_override?: string
  body_html_override?: string
  /** Typed over what the letter prints above its body. */
  letter_header?: LetterHeader
}

/** Only the header values that say something. */
function headerPayload(header: LetterHeader | undefined): LetterHeader | undefined {
  if (!header) return undefined
  const entries = Object.entries(header).filter(
    ([, value]) => typeof value === 'string' && value.trim() !== '',
  )
  return entries.length ? (Object.fromEntries(entries) as LetterHeader) : undefined
}

/**
 * The chosen template rendered against this record, without sending it.
 *
 * Read-before-send: the user signing off on a letter checks the customer's
 * name and the wording here rather than in the sent log.
 */
export function useComposerTemplatePreview(
  recordType: string,
  recordUuid: string | undefined,
  templateId: number | null,
  recipient?: { partyKey?: string; name?: string },
  edit?: LetterEdit,
  promptValues?: Record<string, string>,
) {
  const params: Record<string, string | Record<string, string> | LetterHeader> = {}
  // A picked party is addressed from the record; a typed name only greets.
  if (recipient?.partyKey) params.party_key = recipient.partyKey
  else if (recipient?.name) params.recipient_name = recipient.name
  if (edit?.subject_override) params.subject_override = edit.subject_override
  if (edit?.body_html_override) params.body_html_override = edit.body_html_override
  const header = headerPayload(edit?.letter_header)
  if (header) params.letter_header = header
  if (promptValues && Object.keys(promptValues).length) {
    params.prompt_values = promptValues
  }
  return useQuery<ComposerTemplatePreview>({
    queryKey: ['composer-template-preview', recordType, recordUuid, templateId, params],
    enabled: !!recordUuid && !!templateId,
    // A new addressee re-renders the letter; keep showing the last one until
    // it lands, so nothing being typed is unmounted mid-edit.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      // POST, not GET: an edited letter does not fit in a query string. Long
      // letters already exceed the usual 2,048-character URL limit unedited,
      // and the failure is a 414 or a silently truncated body.
      const res = await api.post(
        `${base(recordType, recordUuid)}/templates/${templateId}/preview/`,
        params,
      )
      return res.data
    },
  })
}

export interface SendAttachmentUpload {
  uuid: string
  file_name: string
  file_size: number
}

/**
 * Upload a file to go with one letter. It is kept with the letter's note, not
 * filed on the record, and only its uploader can attach it (checked on send).
 */
export function useUploadSendAttachment() {
  return useMutation<SendAttachmentUpload, unknown, File>({
    mutationFn: async (file) => {
      const form = new FormData()
      form.append('file', file)
      form.append('use_case', 'CLAIM_PACKET')
      const res = await api.post('/files/upload/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const data = res.data as { uuid: string; file_name?: string; file_size?: number }
      return {
        uuid: data.uuid,
        file_name: data.file_name || file.name,
        file_size: data.file_size ?? file.size,
      }
    },
  })
}
